import { type RefObject } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { type MentionableFile, getDocTypeLabel } from "../../hooks/useMentions";

interface MentionDropdownProps {
  menuRef: RefObject<HTMLDivElement | null>;
  show: boolean;
  filteredFiles: MentionableFile[];
  mentionFilter: string;
  highlightIndex: number;
  onSelect: (file: MentionableFile) => void;
  onHighlight: (index: number) => void;
  position?: "above" | "below";
  compact?: boolean;
}

export function MentionDropdown({
  menuRef,
  show,
  filteredFiles,
  mentionFilter,
  highlightIndex,
  onSelect,
  onHighlight,
  position = "above",
  compact,
}: MentionDropdownProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: position === "above" ? 4 : -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: position === "above" ? 4 : -4 }}
          transition={{ duration: 0.12 }}
          className={cn(
            "absolute left-0 right-0 z-50",
            "rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
            "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)]",
            compact ? "max-h-[140px]" : "max-h-[180px]",
            "overflow-y-auto",
            position === "above" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          <div className="px-2 py-1.5 border-b border-[var(--border-hairline)]">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              Project files
            </span>
          </div>
          {filteredFiles.length === 0 ? (
            <div className="px-2 py-3 text-center text-[10px] text-[var(--text-muted)]">
              {mentionFilter ? "No matching files" : "All files referenced"}
            </div>
          ) : (
            filteredFiles.map((file, i) => (
              <button
                key={file.id}
                onClick={() => onSelect(file)}
                onMouseEnter={() => onHighlight(i)}
                className={cn(
                  "flex items-center gap-2 w-full px-2 py-1.5 text-left",
                  "transition-colors duration-75",
                  i === highlightIndex
                    ? "bg-[var(--bg-surface-hover)]"
                    : "hover:bg-[var(--bg-surface-hover)]",
                )}
              >
                <FileText size={12} className="shrink-0 text-[var(--text-muted)]" />
                <span className="text-[11px] font-medium text-[var(--text-primary)] truncate">
                  {file.filename}
                </span>
                {file.doc_type && (
                  <span className="ml-auto shrink-0 text-[9px] text-[var(--text-muted)]">
                    {getDocTypeLabel(file.doc_type)}
                  </span>
                )}
              </button>
            ))
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
